import AutocompleteWrapper from '@/components/AutocompleteWrapper/AutocompleteWrapper'
import StickyEffect from '@/components/StickyEffect'
import fireMaxRoomsEvent from '@/components/TravelerSelection/ga4'
import type { TypeAheadConfigEndpoint } from '@/components/TypeAhead/types'
import { SearchFormContext } from '@/context/SearchFormContext'
import type { GA4PageNameType } from '@/ga4types'
import useSeti from '@/hooks/useSeti'
import logSearchFormAnalytics from '@/shared-utils/log-search-form-analytics'
import type { LOCATION_SEARCH_TYPE } from '@/types'
import pclnExperimentation from '@pcln/experimentation'
import { useFeature } from '@pcln/feature-components'
import type { HotelFormState, MultiHotelFormState } from '@pcln/horizon-search-form'
import { HotelSearchForm, MultiHotelSearchForm } from '@pcln/horizon-search-form'
import { useCallback, useContext, useMemo, useState } from 'react'
import Sticky from 'react-stickynode'
import { fireBundleAndSaveEvent, fireMultiHotelRadioClick } from '../ga4'
import { handleSubmitForm as handleMultiHotelSubmitForm } from '../SearchForm/MultiHotelSearchForm/utils'
import { groupDealsURL } from '../SearchForm/utils'
import type { HOTEL_FORM_SUBMIT_TYPE, SEM_SEARCH_TYPE } from '../types'
import FormTypeRadios from './HorizonFormTypeRadio'
import HorizonSelectedFilterChipBar from './HorizonSelectedFilterChipBar'
import useHotelFormInitialValues from './useHotelFormInitialValues'

type FormType = {
  onSubmit: (data: HOTEL_FORM_SUBMIT_TYPE, pageName: GA4PageNameType) => void
  setLocation?: (city?: LOCATION_SEARCH_TYPE) => void
  showHotelTypeButtons?: boolean
  pageName?: 'homepage' | 'landing'
}

export default function HorizonHotelForm({
  onSubmit,
  setLocation = () => null,
  showHotelTypeButtons = false,
  pageName = 'homepage',
}: FormType) {
  const { initialValues, isMobile, cguid, SEMSearch, SEMSEARCH } = useHotelFormInitialValues()
  const { updateFormValues } = useContext(SearchFormContext)
  const useV2Typeahead = useFeature('siteExperience.useV2Typeahead') ?? false
  const typeAheadEndpointHotels = useV2Typeahead ? 'hotelsV2' : 'hotels'

  const [hotelType, setHotelType] = useState('single')
  const [isStickyInEffect, setIsStickyInEffect] = useState(false)
  const isHomepage = pageName === 'homepage'
  const enableStickyEffect = !isMobile && pageName === 'homepage' && hotelType === 'single'

  const handleStickyEffectStateChange = ({ status }: Sticky.Status) => {
    if (status === Sticky.STATUS_FIXED && !isStickyInEffect) {
      if (enableStickyEffect) {
        setIsStickyInEffect(true)
      }
    } else if (isStickyInEffect) {
      setIsStickyInEffect(false)
    }
  }

  const isPartnersURL =
    useSeti({ experimentCode: 'HP_LP_HOTELS_TEN_ROOMS', shouldFireImpression: false }) === 'VARIANT'
  const removeBackground =
    useSeti({ experimentCode: 'HP_SEARCH_FORM_HORIZON_CROSS_SELL_DESKTOP' }) === 'VARIANT'

  const aiSearchButtonVariant = useSeti({
    experimentCode: 'USB_AI_SEARCH_BUTTON_HP',
    shouldFireImpression: isHomepage,
  })
  const showAiSearchButton =
    isHomepage && (aiSearchButtonVariant === 'VARIANT1' || aiSearchButtonVariant === 'VARIANT2')

  const recentSearchImprovementVariant = useSeti({
    experimentCode: 'HTL_SEARCH_FRM_RECENT_SEARCH_IMPRV',
    shouldFireImpression: false,
  })
  const mergeRecentAndRecommendedSearches = recentSearchImprovementVariant === 'VARIANT'
  const fireRecentSearchImprovementImpression = useCallback(() => {
    pclnExperimentation.fireImpression('HTL_SEARCH_FRM_RECENT_SEARCH_IMPRV')
  }, [])

  const [trackedValues, setTrackedValues] = useState<Partial<HotelFormState>>({
    endLocation: initialValues.endLocation,
    startDate: initialValues.startDate,
    endDate: initialValues.endDate,
    travelers: initialValues.travelers,
  })

  const hotelPlannerLink = groupDealsURL({
    location: trackedValues.endLocation?.itemName ?? '',
    startDate: trackedValues.startDate ?? '',
    endDate: trackedValues.endDate ?? '',
    isPartnersURL,
  })

  const multiHotelInitialData = useMemo(
    () => ({
      endLocation: trackedValues.endLocation ?? null,
      startDate: trackedValues.startDate ?? '',
      endDate: trackedValues.endDate ?? '',
      travelers: trackedValues.travelers ?? initialValues.travelers,
    }),
    [
      trackedValues.endLocation,
      trackedValues.startDate,
      trackedValues.endDate,
      trackedValues.travelers,
      initialValues.travelers,
    ],
  )

  const handleFormValuesChange = useCallback(
    (values: HotelFormState) => {
      setTrackedValues(values)
      updateFormValues?.(values)
    },
    [updateFormValues],
  )

  const handleSubmit = useCallback(
    (data: HotelFormState) => {
      if (isMobile && typeof window.PCLN?.pclnEventDispatcher?.dispatch === 'function') {
        if (isHomepage) {
          window.PCLN.pclnEventDispatcher.dispatch('FIND YOUR HOTEL')
        }
        window.PCLN.pclnEventDispatcher.dispatch('FIND_YOUR_HOTEL_DATA', {
          cityName: data.endLocation?.cityName,
          cityId: data.endLocation?.cityID,
          checkInDate: data.startDate,
          checkOutDate: data.endDate,
        })
      }

      let userLatitude: number | undefined
      let userLongitude: number | undefined
      if (data.endLocation?.type === 'GEO' && data.endLocation?.lat && data.endLocation?.lon) {
        userLatitude = data.endLocation.lat
        userLongitude = data.endLocation.lon
      }

      const formValues = {
        ...data,
        ...(SEMSearch && { SEMSearch: SEMSearch as SEM_SEARCH_TYPE }),
        ...(SEMSEARCH && { SEMSEARCH: SEMSEARCH as SEM_SEARCH_TYPE }),
        useCurrentLocation: data.endLocation?.type === 'GEO',
        ...(userLatitude && userLongitude && { userLatitude, userLongitude }),
      }

      onSubmit(formValues, pageName)
      logSearchFormAnalytics({ cguid, pageName, productType: 'hotels', isMobile })
    },
    [isMobile, isHomepage, SEMSearch, SEMSEARCH, onSubmit, pageName, cguid],
  )

  const handleMultiHotelSubmit = useCallback(
    (data: MultiHotelFormState) => {
      handleMultiHotelSubmitForm(data)
      logSearchFormAnalytics({ cguid, pageName, productType: 'multi-hotel', isMobile })
    },
    [cguid, isMobile, pageName],
  )

  return (
    <div className='flex w-auto flex-col gap-3'>
      {showHotelTypeButtons && (
        <FormTypeRadios
          formType={hotelType}
          onChange={(selectedFormName) => {
            setHotelType(selectedFormName)
            fireMultiHotelRadioClick(pageName)
          }}
        />
      )}
      <StickyEffect
        enabled={enableStickyEffect}
        isStickyInEffect={isStickyInEffect}
        handleStickyEffectStateChange={handleStickyEffectStateChange}
        innerZ={isMobile ? 0 : 3}
      >
        {hotelType === 'multi' && showHotelTypeButtons ? (
          <MultiHotelSearchForm
            initialData={multiHotelInitialData}
            isMobile={isMobile}
            autoAdvance={!isMobile}
            typeAheadEndpoint={typeAheadEndpointHotels}
            onSubmit={handleMultiHotelSubmit}
            renderAutocomplete={(props) => (
              <AutocompleteWrapper
                ref={props.inputRef}
                searchKey={props.searchKey}
                label={props.label}
                typeAheadEndpoint={props.typeAheadEndpoint as TypeAheadConfigEndpoint}
                errorMessage={props.errorMessage}
                initialSelectedItem={props.initialSelectedItem}
                onItemSelect={(item) => props.onItemSelect(item)}
                isMobile={isMobile}
                mergeRecentAndRecommendedSearches={mergeRecentAndRecommendedSearches}
                onRecentSearchImprovementImpression={fireRecentSearchImprovementImpression}
              />
            )}
          />
        ) : (
          <HotelSearchForm
            initialValues={initialValues}
            isMobile={isMobile}
            autoAdvance={!isMobile}
            typeAheadEndpoint={typeAheadEndpointHotels}
            setLocation={setLocation}
            isStickyInEffect={isStickyInEffect}
            hotelPlannerLinkHref={hotelPlannerLink}
            removeBackground={removeBackground}
            onMaxRoomsClick={() => {
              pclnExperimentation.fireImpression('HP_LP_HOTELS_TEN_ROOMS')
              window.open(hotelPlannerLink, '_blank')
              fireMaxRoomsEvent(pageName, 'hotel')
            }}
            onBundleChange={(tripType, checked) => {
              if (checked) {
                try {
                  fireBundleAndSaveEvent(tripType)
                } catch {
                  // GA4TripTypeMapper may throw on unexpected trip type combinations
                }
              }
            }}
            onFormValuesChange={handleFormValuesChange}
            onSubmit={handleSubmit}
            renderAutocomplete={(props) => (
              <AutocompleteWrapper
                ref={props.inputRef}
                searchKey={props.searchKey}
                label={props.label}
                typeAheadEndpoint={props.typeAheadEndpoint as TypeAheadConfigEndpoint}
                errorMessage={props.errorMessage}
                initialSelectedItem={props.initialSelectedItem}
                onItemSelect={(item) => props.onItemSelect(item)}
                isMobile={isMobile}
                showCurrentLocation={props.showCurrentLocation}
                onGeolocationError={props.onGeolocationError}
                selectedFilterChipData={props.selectedFilterChipData}
                setSelectedFilterChipData={props.setSelectedFilterChipData}
                showFilterChips={props.showFilterChips}
                aiSearchButtonVariant={showAiSearchButton ? aiSearchButtonVariant : undefined}
                mergeRecentAndRecommendedSearches={mergeRecentAndRecommendedSearches}
                onRecentSearchImprovementImpression={fireRecentSearchImprovementImpression}
              />
            )}
            renderSelectedFilterChipBar={(chipBarProps) => (
              <HorizonSelectedFilterChipBar
                selectedFilterChipData={chipBarProps.selectedFilterChipData}
                setSelectedFilterChipData={chipBarProps.setSelectedFilterChipData}
              />
            )}
          />
        )}
      </StickyEffect>
    </div>
  )
}
